/**
 * Support Monitor filters. `from` is inclusive and `to` is exclusive.
 * Omit both for the last 30 days ending at `now`. The window cannot be
 * longer than 366 days. Date-only ISO (`2026-10-01`) is UTC midnight.
 */
import { z } from 'zod';
export const SUPPORT_MONITOR_CHANNEL_KINDS = [
    'telegram',
    'facebook',
    'instagram',
    'whatsapp',
    'email_imap',
    'phone',
    'manual',
];
const DAY_MS = 24 * 60 * 60 * 1000;
export const SUPPORT_MONITOR_DEFAULT_DAYS = 30;
export const SUPPORT_MONITOR_MAX_RANGE_DAYS = 366;
const WEEKLY_AFTER_MS = 90 * DAY_MS;
const isoInstant = z.string().refine((value) => Number.isFinite(Date.parse(value)), {
    message: 'Use an ISO date',
});
export const SupportMonitorQuerySchema = z.object({
    from: isoInstant.optional(),
    to: isoInstant.optional(),
    assigneeId: z.string().uuid().optional(),
    priorityId: z.string().uuid().optional(),
    channelKind: z.enum(SUPPORT_MONITOR_CHANNEL_KINDS).optional(),
    contractTypeId: z.string().uuid().optional(),
    contractId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
});
/** Parses the query and fills the window. `now` is the end of the default range. */
export function parseSupportMonitorQuery(input, now) {
    return SupportMonitorQuerySchema.superRefine((data, ctx) => {
        const to = data.to ? new Date(data.to) : now;
        const from = data.from ? new Date(data.from) : new Date(to.getTime() - SUPPORT_MONITOR_DEFAULT_DAYS * DAY_MS);
        if (!(from < to)) {
            ctx.addIssue({ code: 'custom', path: ['from'], message: 'from must be before to' });
            return;
        }
        if (to.getTime() - from.getTime() > SUPPORT_MONITOR_MAX_RANGE_DAYS * DAY_MS) {
            ctx.addIssue({
                code: 'custom',
                path: ['to'],
                message: 'Date range cannot exceed 366 days',
            });
        }
    })
        .transform((data) => {
        const to = data.to ? new Date(data.to) : now;
        const from = data.from
            ? new Date(data.from)
            : new Date(to.getTime() - SUPPORT_MONITOR_DEFAULT_DAYS * DAY_MS);
        const span = to.getTime() - from.getTime();
        const query = {
            from,
            to,
            previousTo: from,
            previousFrom: new Date(from.getTime() - span),
            bucket: span > WEEKLY_AFTER_MS ? 'week' : 'day',
        };
        if (data.assigneeId)
            query.assigneeId = data.assigneeId;
        if (data.priorityId)
            query.priorityId = data.priorityId;
        if (data.channelKind)
            query.channelKind = data.channelKind;
        if (data.contractTypeId)
            query.contractTypeId = data.contractTypeId;
        if (data.contractId)
            query.contractId = data.contractId;
        if (data.stageId)
            query.stageId = data.stageId;
        return query;
    })
        .parse(input);
}
//# sourceMappingURL=support-monitor.schema.js.map